/**
 * LeftShield Portal - Control Plane
 *
 * The primary landing page, positioned as the governance control plane.
 * Layout: Header → Governance Loop Visual → Executive Status → Risk Concentration →
 *         Proof & Assurance → Action Layer
 */
import React from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Caption1,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Card,
  CardHeader,
  tokens,
  Badge,
} from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  ShieldCheckmarkRegular,
  CertificateRegular,
  DataTrendingRegular,
  PlayRegular,
  LockClosedRegular,
  LightbulbRegular,
  ArrowRightRegular,
  BotRegular,
  WarningRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useDashboardOverview } from '../hooks/useDashboardOverview';
import { useScoreSummary } from '../hooks/useScoreSummary';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { ActivationChecklist } from '../components/common/ActivationChecklist';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import {
  GovernancePipelineFlow,
  MetricCard,
  governanceTokens,
} from '../components/design-system';
import type { PipelineStep } from '../components/design-system';

export const HomePage: React.FC = () => {
  const ps = usePageStyles();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useDashboardOverview();
  const { data: scoreSummary } = useScoreSummary();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Spinner size="large" label="Loading control plane..." />
      </div>
    );
  }

  if (isError || !data) {
    const statusCode = (error as any)?.status ?? (error as any)?.response?.status;
    const isUnauth = statusCode === 401 || (error as Error)?.message?.includes('MSAL');

    if (isUnauth) {
      return (
        <div className={ps.page}>
          <PageHeader
            title="Control Plane"
            subtitle="LeftShield observes, enforces, and proves compliance continuously across your SDLC."
          />
          <UnauthenticatedBanner feature="control plane data" />
        </div>
      );
    }

    const message = statusCode === 403
      ? 'You do not have permission to access control plane data.'
      : (error as Error)?.message || 'An unexpected error occurred.';

    return (
      <div className={ps.page}>
        <PageHeader
          title="Control Plane"
          subtitle="LeftShield observes, enforces, and proves compliance continuously across your SDLC."
        />
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Failed to load control plane data</MessageBarTitle>
            <Body1 style={{ color: tokens.colorNeutralForeground1 }}>{message}</Body1>
          </MessageBarBody>
        </MessageBar>
        <Button appearance="primary" icon={<ArrowClockwiseRegular />} onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  // ─── Derive metrics ────────────────────────────────────────────────────────
  const enforcementRate = data.enforcementRate ?? 0;
  const policyEvaluations = data.enforcement?.totalEvaluations ?? 0;
  const blockedCount = data.enforcement?.blockedCount ?? 0;
  const allowedCount = data.enforcement?.allowedCount ?? 0;
  const evidenceCount = data.evidenceChain?.totalEvaluations ?? 0;
  const evidenceIntegrity = data.evidenceChain?.isValid ? 100 : 0;
  const criticalFindings = data.criticalFindings ?? 0;
  const governanceScore = data.governanceScore ?? 0;
  const postureScore = data.postureScore ?? 0;
  const repoCount = data.integrationHealth?.totalIntegrations ?? 0;
  const isUnconfigured = postureScore === 0 && criticalFindings === 0 && data.enforcementRate == null;
  const frameworkCount = scoreSummary?.complianceScore?.value != null ? 1 : 0;

  // ─── Section 2: Governance Loop Pipeline ──────────────────────────────────
  const pipelineSteps: PipelineStep[] = [
    {
      id: 'signals',
      label: 'Signals',
      icon: <DataTrendingRegular />,
      status: repoCount > 0 ? 'success' : 'inactive',
      metadata: `${repoCount} collectors`,
    },
    {
      id: 'policy-engine',
      label: 'Policy Engine',
      icon: <ShieldCheckmarkRegular />,
      status: policyEvaluations > 0 ? 'success' : 'inactive',
      metadata: policyEvaluations > 0 ? `${Math.max(1, Math.floor(policyEvaluations / 10))} active` : 'Awaiting policies',
    },
    {
      id: 'enforcement',
      label: 'Runtime Enforcement',
      icon: <PlayRegular />,
      status: blockedCount > 0 ? 'warning' : policyEvaluations > 0 ? 'success' : 'inactive',
      metadata: policyEvaluations > 0 ? `${policyEvaluations} evaluated, ${blockedCount} blocked` : 'Awaiting first evaluation',
    },
    {
      id: 'evidence',
      label: 'Evidence Chain',
      icon: <CertificateRegular />,
      status: evidenceCount > 0 ? 'success' : 'inactive',
      metadata: evidenceCount > 0 ? `${evidenceCount} verified` : 'Awaiting first evidence',
    },
    {
      id: 'assurance',
      label: 'Framework Assurance',
      icon: <LockClosedRegular />,
      status: governanceScore > 60 ? 'success' : governanceScore > 0 ? 'warning' : 'inactive',
      metadata: frameworkCount > 0 ? `${frameworkCount} enabled` : 'Frameworks ready for activation',
    },
    {
      id: 'intelligence',
      label: 'Intelligence',
      icon: <LightbulbRegular />,
      status: (data.topGaps?.length ?? 0) > 0 ? 'warning' : governanceScore > 0 ? 'success' : 'inactive',
      metadata: (data.topGaps?.length ?? 0) > 0 ? `${data.topGaps!.length} gaps` : 'No gaps detected',
    },
  ];

  // ─── Section 4: Risk Concentration ────────────────────────────────────────
  const topGaps = (data.topGaps ?? []).slice(0, 3);
  const topFindings = (data.topFindings ?? []).slice(0, 3);

  // ─── Section 6: Recommended Actions ───────────────────────────────────────
  const actions: { title: string; description: string; path: string }[] = [];
  if (repoCount === 0) {
    actions.push({ title: 'Connect your first repository', description: 'Link a GitHub or Azure DevOps repo to start governance.', path: '/integrations' });
  }
  if (policyEvaluations === 0) {
    actions.push({ title: 'Configure governance policies', description: 'Set up enforcement policies to protect your delivery pipelines.', path: '/guardrails' });
  }
  if (frameworkCount === 0) {
    actions.push({ title: 'Enable a compliance framework', description: 'Map controls to SOC2, ISO 27001, or Essential Eight.', path: '/frameworks' });
  }
  if (criticalFindings > 0) {
    actions.push({ title: `Resolve ${criticalFindings} critical findings`, description: 'Address high-severity issues before they propagate.', path: '/security' });
  }
  if (actions.length === 0) {
    actions.push({ title: 'Review governance posture', description: 'Your control plane is active. Review your current posture.', path: '/compliance' });
  }

  return (
    <div className={ps.page}>
      {/* ─── Section 1: Page Header ──────────────────────────────────────────── */}
      <PageHeader
        title="Control Plane"
        subtitle="LeftShield observes, enforces, and proves compliance continuously across your SDLC."
        showAIButton={false}
        actions={
          <Button
            appearance="primary"
            icon={<BotRegular />}
            onClick={() => navigate('/ai-advisor')}
          >
            Ask LeftShield AI
          </Button>
        }
      />

      {/* Activation Checklist — shown when tenant is unconfigured */}
      {isUnconfigured && <ActivationChecklist />}

      {/* ─── Section 2: Core Governance Loop Visual (Hero) ───────────────────── */}
      <Card>
        <CardHeader
          header={<Title2>Governance Loop</Title2>}
          description="Each stage feeds the next — creating a compounding governance loop that strengthens enforcement and assurance over time."
        />
        <div className={ps.cardBody}>
          <GovernancePipelineFlow steps={pipelineSteps} activeStep="enforcement" />
        </div>
      </Card>

      {/* ─── Section 3: Executive Status Row ─────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: tokens.spacingHorizontalL,
      }}>
        <MetricCard
          title="Governance Score"
          value={governanceScore > 0 ? governanceScore : '—'}
          unit={governanceScore > 0 ? '%' : undefined}
          icon={<DataTrendingRegular />}
          accentColor={governanceTokens.colorGovernancePrimary}
          secondaryMetrics={[
            { label: 'Controls', value: `${data.pillars?.controlCoverage ?? 0}%` },
            { label: 'Evidence', value: `${data.pillars?.evidenceIntegrity ?? 0}%` },
          ]}
          trend={
            governanceScore > 60
              ? { direction: 'up', label: 'Strong', sentiment: 'positive' }
              : governanceScore > 0
                ? { direction: 'stable', label: 'Building', sentiment: 'neutral' }
                : { direction: 'stable', label: 'Awaiting first evaluation', sentiment: 'neutral' }
          }
        />
        <MetricCard
          title="Enforcement Rate"
          value={enforcementRate > 0 ? enforcementRate : '—'}
          unit={enforcementRate > 0 ? '%' : undefined}
          icon={<ShieldCheckmarkRegular />}
          accentColor={governanceTokens.colorGovernanceSecondary}
          secondaryMetrics={[
            { label: 'Evaluations', value: policyEvaluations },
            { label: 'Blocked', value: blockedCount },
            { label: 'Allowed', value: allowedCount },
          ]}
          trend={
            enforcementRate > 80
              ? { direction: 'up', label: 'High coverage', sentiment: 'positive' }
              : enforcementRate > 0
                ? { direction: 'stable', label: 'Partial coverage', sentiment: 'neutral' }
                : { direction: 'stable', label: 'Not yet active', sentiment: 'neutral' }
          }
        />
        <MetricCard
          title="Evidence Confidence"
          value={evidenceCount > 0 ? evidenceIntegrity : '—'}
          unit={evidenceCount > 0 ? '%' : undefined}
          icon={<CertificateRegular />}
          accentColor={governanceTokens.colorGovernancePrimary}
          secondaryMetrics={[
            { label: 'Artifacts', value: evidenceCount },
            { label: 'Frameworks', value: frameworkCount },
          ]}
          trend={
            evidenceCount > 0
              ? { direction: 'up', label: 'Verified', sentiment: 'positive' }
              : { direction: 'stable', label: 'Awaiting first evidence', sentiment: 'neutral' }
          }
        />
        <MetricCard
          title="Material Exposure"
          value={criticalFindings}
          unit="critical"
          icon={<WarningRegular />}
          accentColor={criticalFindings > 0 ? governanceTokens.colorStatusError : governanceTokens.colorStatusSuccess}
          secondaryMetrics={[
            { label: 'High', value: data.severityBreakdown?.high ?? 0 },
            { label: 'Medium', value: data.severityBreakdown?.medium ?? 0 },
          ]}
          trend={
            criticalFindings > 0
              ? { direction: 'up', label: `${criticalFindings} critical`, sentiment: 'negative' }
              : { direction: 'stable', label: 'No critical findings', sentiment: 'positive' }
          }
        />
      </div>

      {/* ─── Section 4: Risk Concentration (Two-column) ──────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: tokens.spacingHorizontalL,
      }}>
        {/* Top Governance Gaps */}
        <Card>
          <CardHeader header={<Title3>Top Governance Gaps</Title3>} />
          <div className={ps.cardBody} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
            {topGaps.length > 0 ? topGaps.map((gap, i) => (
              <div key={i} style={{
                padding: tokens.spacingVerticalS,
                backgroundColor: tokens.colorNeutralBackground2,
                borderRadius: tokens.borderRadiusMedium,
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacingVerticalXS,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Body1 style={{ fontWeight: 600 }}>{gap.pillar}</Body1>
                  <Badge
                    appearance="filled"
                    color={gap.severity === 'critical' ? 'danger' : gap.severity === 'high' ? 'warning' : 'informative'}
                  >
                    {gap.severity}
                  </Badge>
                </div>
                <Body2 style={{ color: tokens.colorNeutralForeground2 }}>{gap.description}</Body2>
                <Caption1 style={{ color: governanceTokens.colorGovernancePrimary }}>{gap.recommendedAction}</Caption1>
              </div>
            )) : (
              <Body2 style={{ color: tokens.colorNeutralForeground3, padding: tokens.spacingVerticalL, textAlign: 'center' }}>
                No governance gaps detected
              </Body2>
            )}
          </div>
        </Card>

        {/* Top Exposure Concentrations */}
        <Card>
          <CardHeader header={<Title3>Top Exposure Concentrations</Title3>} />
          <div className={ps.cardBody} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
            {topFindings.length > 0 ? topFindings.map((finding) => (
              <div key={finding.id} style={{
                padding: tokens.spacingVerticalS,
                backgroundColor: tokens.colorNeutralBackground2,
                borderRadius: tokens.borderRadiusMedium,
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacingVerticalXS,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Body1 style={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {finding.title}
                  </Body1>
                  <Badge
                    appearance="filled"
                    color={finding.severity === 'critical' ? 'danger' : finding.severity === 'high' ? 'warning' : 'informative'}
                    style={{ flexShrink: 0, marginLeft: tokens.spacingHorizontalS }}
                  >
                    {finding.severity}
                  </Badge>
                </div>
                {finding.filePath && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{finding.filePath}</Caption1>
                )}
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Status: {finding.status} {finding.frameworkCode && `| ${finding.frameworkCode}`}
                </Caption1>
              </div>
            )) : (
              <Body2 style={{ color: tokens.colorNeutralForeground3, padding: tokens.spacingVerticalL, textAlign: 'center' }}>
                No active exposures detected
              </Body2>
            )}
          </div>
        </Card>
      </div>

      {/* ─── Section 5: Proof & Assurance ────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: tokens.spacingHorizontalL,
      }}>
        <Card>
          <CardHeader header={<Title3>Framework Coverage</Title3>} />
          <div className={ps.cardBody} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Body2>Governance Score</Body2>
              <Body2 style={{ fontWeight: 600 }}>{governanceScore > 0 ? `${governanceScore}%` : 'Not yet calculated'}</Body2>
            </div>
            <CoverageBar label="Control Coverage" value={data.pillars?.controlCoverage ?? 0} />
            <CoverageBar label="Enforcement Gaps" value={100 - (data.pillars?.enforcementGaps ?? 100)} />
            <CoverageBar label="Evidence Confidence" value={data.pillars?.evidenceIntegrity ?? 0} />
          </div>
        </Card>

        <Card>
          <CardHeader header={<Title3>Evidence Chain Status</Title3>} />
          <div className={ps.cardBody} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <CheckmarkCircleRegular style={{ color: evidenceCount > 0 ? governanceTokens.colorStatusSuccess : tokens.colorNeutralForeground3 }} />
              <Body2>{evidenceCount > 0 ? `${evidenceCount} artifacts verified` : 'No evidence artifacts yet'}</Body2>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Body2>Chain Integrity</Body2>
              <Body2 style={{ fontWeight: 600, color: evidenceIntegrity === 100 ? governanceTokens.colorStatusSuccess : tokens.colorNeutralForeground3 }}>
                {evidenceCount > 0 ? (evidenceIntegrity === 100 ? 'Valid' : 'Unverified') : 'Awaiting first evidence'}
              </Body2>
            </div>
            {data.evidenceChain?.lastVerifiedAt && (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Last verified: {formatTimeAgo(new Date(data.evidenceChain.lastVerifiedAt))}
              </Caption1>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader header={<Title3>Continuous Assurance</Title3>} />
          <div className={ps.cardBody} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
            <AssuranceCheckItem label="Repositories connected" met={repoCount > 0} />
            <AssuranceCheckItem label="Policies configured" met={policyEvaluations > 0} />
            <AssuranceCheckItem label="Evidence being generated" met={evidenceCount > 0} />
            <AssuranceCheckItem label="Framework mapped" met={frameworkCount > 0} />
            <AssuranceCheckItem label="No critical findings" met={criticalFindings === 0} />
          </div>
        </Card>
      </div>

      {/* ─── Section 6: Action Layer (Two-column) ────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: tokens.spacingHorizontalL,
      }}>
        {/* Recommended Actions */}
        <Card>
          <CardHeader header={<Title3>Recommended Actions</Title3>} />
          <div className={ps.cardBody} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
            {actions.slice(0, 3).map((action, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacingHorizontalM,
                  padding: tokens.spacingVerticalS,
                  backgroundColor: tokens.colorNeutralBackground2,
                  borderRadius: tokens.borderRadiusMedium,
                  cursor: 'pointer',
                }}
                onClick={() => navigate(action.path)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(action.path); }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Body1 style={{ fontWeight: 600 }}>{action.title}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{action.description}</Caption1>
                </div>
                <ArrowRightRegular style={{ color: governanceTokens.colorGovernancePrimary, flexShrink: 0 }} />
              </div>
            ))}
          </div>
        </Card>

        {/* AI Summary */}
        <Card>
          <CardHeader
            header={<Title3>AI Summary</Title3>}
            description="What changed, why it matters, what to do next"
          />
          <div className={ps.cardBody} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
            <Body2 style={{ color: tokens.colorNeutralForeground2, lineHeight: '1.6' }}>
              {generateAISummary(data, repoCount, policyEvaluations, blockedCount, evidenceCount, criticalFindings, governanceScore)}
            </Body2>
            <Button
              appearance="subtle"
              icon={<BotRegular />}
              onClick={() => navigate('/ai-advisor')}
              style={{ alignSelf: 'flex-start' }}
            >
              Ask LeftShield AI for more
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// ─── Helper: Time-ago formatter ──────────────────────────────────────────────
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

// ─── Helper: Coverage bar ────────────────────────────────────────────────────
const CoverageBar: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Body2>{label}</Body2>
      <Body2 style={{ fontWeight: 600 }}>{value > 0 ? `${value}%` : 'No data yet'}</Body2>
    </div>
    <div style={{
      height: '8px',
      backgroundColor: tokens.colorNeutralBackground3,
      borderRadius: tokens.borderRadiusCircular,
      overflow: 'hidden',
    }}>
      <div style={{
        height: '100%',
        width: `${Math.min(value, 100)}%`,
        backgroundColor: value >= 80
          ? governanceTokens.colorStatusSuccess
          : value >= 40
            ? governanceTokens.colorStatusWarning
            : value > 0
              ? governanceTokens.colorStatusError
              : tokens.colorNeutralBackground3,
        borderRadius: tokens.borderRadiusCircular,
        transition: 'width 0.5s ease',
      }} />
    </div>
  </div>
);

// ─── Helper: Assurance check item ────────────────────────────────────────────
const AssuranceCheckItem: React.FC<{ label: string; met: boolean }> = ({ label, met }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
    <CheckmarkCircleRegular
      style={{ color: met ? governanceTokens.colorStatusSuccess : tokens.colorNeutralForeground4, fontSize: '16px' }}
    />
    <Body2 style={{ color: met ? tokens.colorNeutralForeground1 : tokens.colorNeutralForeground3 }}>
      {label}
    </Body2>
  </div>
);

// ─── Helper: Generate contextual AI summary text ─────────────────────────────
function generateAISummary(
  _data: any,
  repoCount: number,
  policyEvaluations: number,
  blockedCount: number,
  evidenceCount: number,
  criticalFindings: number,
  governanceScore: number,
): string {
  const parts: string[] = [];

  if (repoCount === 0) {
    parts.push('No repositories are connected yet. Connect your first repository to begin governance coverage.');
  } else {
    parts.push(`LeftShield is actively governing ${repoCount} ${repoCount === 1 ? 'repository' : 'repositories'}.`);
  }

  if (policyEvaluations > 0) {
    parts.push(`${policyEvaluations} policy evaluations have been executed${blockedCount > 0 ? `, with ${blockedCount} deployments blocked for policy violations` : ', with no blocks required'}.`);
  }

  if (evidenceCount > 0) {
    parts.push(`${evidenceCount} compliance evidence artifacts have been generated and verified.`);
  }

  if (criticalFindings > 0) {
    parts.push(`There are ${criticalFindings} critical findings requiring immediate attention.`);
  }

  if (governanceScore > 0) {
    parts.push(`Overall governance score is ${governanceScore}%${governanceScore >= 80 ? ' — strong posture.' : governanceScore >= 50 ? ' — improvement opportunities exist.' : ' — significant gaps need attention.'}`);
  }

  return parts.join(' ') || 'Your control plane is initializing. Connect integrations and configure policies to get started.';
}
